<template>
  <div class="tesla-coil-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$f = 1/(2π√LC) | 高频高压谐振变压器$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <!-- The Primary Coil (Base) -->
          <rect x="160" y="200" width="80" height="20" fill="none" stroke="#cd7f32" stroke-width="4" rx="5" />
          
          <!-- The Secondary Coil (Vertical) -->
          <rect x="185" y="80" width="30" height="120" fill="none" stroke="#888" stroke-width="1" />
          <line v-for="i in 20" :key="i" x1="185" :y1="82 + i*6" x2="215" :y2="82 + i*6" stroke="#444" stroke-width="0.5" />

          <!-- The Topload (Toroid) -->
          <ellipse cx="200" cy="80" rx="40" ry="15" fill="#aaa" stroke="#fff" />

          <!-- The Sparks (Streamers) -->
          <g v-if="active">
             <path v-for="(spark, idx) in sparkles" :key="idx" :d="spark" fill="none" :stroke="sparkColor" stroke-width="1.5" opacity="0.8">
                <animate attributeName="opacity" values="0;1;0" dur="0.1s" repeatCount="indefinite" />
             </path>
             <!-- Glow -->
             <circle cx="200" cy="80" r="60" fill="url(#sparkGlow)" opacity="0.4" />
          </g>

          <!-- Label -->
          <text x="30" y="55" fill="#64748b" font-size="10" font-weight="bold">高频谐振磁场</text>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>输出电压: {{ voltage }} kV</label>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
           <el-button type="danger" size="mini" @mousedown.native="active = true" @mouseup.native="active = false" @mouseleave.native="active = false" @touchstart.native.prevent="active = true" @touchend.native="active = false">按住击穿空气 (放电)</el-button>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>特斯拉线圈是一种利用**谐振原理**产生极高电压、低电流、高频率交流电的变压器。它由两组 LC 谐振电路组成：初级线圈产生高频振荡，其磁场在次级线圈（匝数极多）中感应出电动势。当两组电路的谐振频率完全匹配时，次级线圈顶部的电压会累积到足以击穿空气的程度，产生壮观的电弧。它体现了电磁感应与无线能量传输的早期构想。</p>
        </div>
      </div>
    </div>
    
    <svg height="0" width="0" style="position:absolute">
      <defs>
        <radialGradient id="sparkGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" style="stop-color:#8e44ad;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#8e44ad;stop-opacity:0" />
        </radialGradient>
      </defs>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'TeslaCoilViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      active: false,
      sparkles: [],
      voltage: 0,
      timer: null
    }
  },
  computed: {
    sparkColor() { return "#a29bfe"; }
  },
  watch: {
    active(val) {
      if (val) {
        this.startSparking();
      } else {
        this.stopSparking();
      }
    }
  },
  methods: {
    startSparking() {
      this.timer = setInterval(() => {
        this.voltage = (200 + Math.random() * 50).toFixed(1);
        this.generateSparks();
      }, 80);
    },
    stopSparking() {
      clearInterval(this.timer);
      this.voltage = 0;
      this.sparkles = [];
    },
    generateSparks() {
      const newSparks = [];
      for (let n = 0; n < 5; n++) {
        let startX = 200 + (Math.random() - 0.5) * 60;
        let startY = 80 + (Math.random() - 0.5) * 10;
        const points = [`${startX.toFixed(1)},${startY.toFixed(1)}`];
        let cx = startX;
        let cy = startY;
        for (let i = 0; i < 5; i++) {
          cx += (Math.random() - 0.5) * 40;
          cy -= Math.random() * 20 + 5;
          points.push(`${cx.toFixed(1)},${cy.toFixed(1)}`);
        }
        newSparks.push("M " + points.join(" L "));
      }
      this.sparkles = newSparks;
    }
  },
  beforeDestroy() {
    this.stopSparking();
  }
}
</script>

<style scoped lang="scss">
.tesla-coil-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header { display: flex; justify-content: space-between; align-items: center; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { color: #8e44ad; background: rgba(142, 68, 173, 0.1); padding: 4px 12px; border-radius: 8px; font-weight: bold; } }
.canvas-box { background: #020205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.hud-top { position: absolute; top: 12px; left: 12px; .stat { background: rgba(15, 23, 42, 0.8); padding: 6px 12px; border-radius: 8px; border-left: 3px solid #a29bfe; label { font-size: 10px; color: #94a3b8; display: block; } font-size: 14px; color: #fff; font-weight: bold; } }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.03); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(142, 68, 173, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
